import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Wrench } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card, CardHeader } from '@/components/common/Card';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { Spinner } from '@/components/common/Spinner';
import { SelectField, TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect } from '@/components/tables/ListPage';
import { Pagination } from '@/components/tables/Pagination';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { maintenanceApi, vehiclesApi } from '@/services/api/endpoints/transport';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type {
  MaintenanceInput,
  MaintenanceRecord,
  MaintenanceSchedule,
  MaintenanceScheduleInput,
  MaintenanceStatus,
  MaintenanceType,
} from '@/types/transport';
import { formatDate, orDash } from '@/utils/format';
import { Badge, MaintenanceStatusBadge, Money, humanise } from './shared';

const TYPES: MaintenanceType[] = [
  'ROUTINE_SERVICE',
  'OIL_CHANGE',
  'TIRE_REPLACEMENT',
  'BRAKE_SERVICE',
  'ENGINE_REPAIR',
  'ELECTRICAL_REPAIR',
  'BODY_REPAIR',
  'INSPECTION',
  'OTHER',
];

const STATUSES: { value: MaintenanceStatus; label: string }[] = [
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

/** The service-record form, held as strings the way the inputs hold them. */
interface RecordDraft {
  id?: string;
  vehicleId: string;
  maintenanceType: MaintenanceType | '';
  serviceDate: string;
  mileageKm: string;
  description: string;
  serviceProvider: string;
  partsCost: string;
  laborCost: string;
  totalCost: string;
  nextServiceDate: string;
  nextServiceMileage: string;
  notes: string;
}

const EMPTY_RECORD: RecordDraft = {
  vehicleId: '',
  maintenanceType: '',
  serviceDate: '',
  mileageKm: '',
  description: '',
  serviceProvider: '',
  partsCost: '',
  laborCost: '',
  totalCost: '',
  nextServiceDate: '',
  nextServiceMileage: '',
  notes: '',
};

interface ScheduleDraft {
  maintenanceType: MaintenanceType | '';
  intervalMonths: string;
  intervalKm: string;
  lastServiceDate: string;
  lastServiceMileage: string;
  notes: string;
}

const EMPTY_SCHEDULE: ScheduleDraft = {
  maintenanceType: '',
  intervalMonths: '',
  intervalKm: '',
  lastServiceDate: '',
  lastServiceMileage: '',
  notes: '',
};

const num = (value: string) => (value.trim() === '' ? undefined : Number(value));
const text = (value: string) => (value.trim() === '' ? undefined : value.trim());

/**
 * Keeping the metal roadworthy.
 *
 * <p>Three layers on one page, because they are one job: what is due (the
 * server's verdict from schedules against today and each odometer), what is
 * booked or done (the records), and the standing intervals that produce the
 * due list. Starting work benches the vehicle and completing releases it — a
 * fact the fleet screens reflect, which is why record mutations invalidate the
 * vehicle cache too.
 *
 * <p>Costs are reporting figures. The total defaults to parts plus labour on
 * the server; this form never adds the two itself.
 */
export function MaintenancePage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('serviceDate,desc');

  const manages = can(PERMISSIONS.VEHICLE_MAINTENANCE_MANAGE);

  const [vehicleFilter, setVehicleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<MaintenanceStatus | ''>('');
  const [draft, setDraft] = useState<RecordDraft | null>(null);
  const [scheduleVehicleId, setScheduleVehicleId] = useState('');
  const [scheduleDraft, setScheduleDraft] = useState<ScheduleDraft | null>(null);
  const [removing, setRemoving] = useState<MaintenanceSchedule | null>(null);

  const edit = (patch: Partial<RecordDraft>) =>
    setDraft((current) => (current === null ? current : { ...current, ...patch }));
  const editSchedule = (patch: Partial<ScheduleDraft>) =>
    setScheduleDraft((current) => (current === null ? current : { ...current, ...patch }));

  const params = {
    ...table.params,
    ...(vehicleFilter === '' ? {} : { vehicleId: vehicleFilter }),
    ...(statusFilter === '' ? {} : { status: statusFilter }),
  };

  const records = useQuery({
    queryKey: queryKeys.maintenance.list(params),
    queryFn: () => maintenanceApi.list(params),
  });

  const due = useQuery({
    queryKey: queryKeys.maintenance.due,
    queryFn: () => maintenanceApi.due(),
  });

  const vehicles = useQuery({
    queryKey: queryKeys.vehicles.list({ page: 0, size: 200 }),
    queryFn: () => vehiclesApi.list({ page: 0, size: 200 }),
    enabled: can(PERMISSIONS.VEHICLE_VIEW),
  });

  const schedules = useQuery({
    queryKey: queryKeys.vehicles.schedules(scheduleVehicleId),
    queryFn: () => maintenanceApi.schedules(scheduleVehicleId),
    enabled: scheduleVehicleId !== '',
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.maintenance.all });
    // A job in progress benches the vehicle; a completed one rolls its
    // odometer forward — the fleet cache is stale either way.
    void queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.all });
  };

  const save = useMutation({
    mutationFn: () => {
      const form = draft as RecordDraft;
      const payload: MaintenanceInput = {
        vehicleId: form.vehicleId,
        maintenanceType: form.maintenanceType as MaintenanceType,
        serviceDate: text(form.serviceDate),
        mileageKm: num(form.mileageKm),
        description: text(form.description),
        serviceProvider: text(form.serviceProvider),
        partsCost: num(form.partsCost),
        laborCost: num(form.laborCost),
        totalCost: num(form.totalCost),
        nextServiceDate: text(form.nextServiceDate),
        nextServiceMileage: num(form.nextServiceMileage),
        notes: text(form.notes),
      };
      return form.id === undefined
        ? maintenanceApi.create(payload)
        : maintenanceApi.update(form.id, payload);
    },
    onSuccess: (record) => {
      toast.success(`${record.vehicleName} — ${humanise(record.maintenanceType)} saved`);
      setDraft(null);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const setStatus = useMutation({
    mutationFn: (input: { id: string; status: MaintenanceStatus }) =>
      maintenanceApi.setStatus(input.id, input.status),
    onSuccess: (record) => {
      toast.success(`${record.vehicleName} — now ${humanise(record.status)}`);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const saveSchedule = useMutation({
    mutationFn: () => {
      const form = scheduleDraft as ScheduleDraft;
      const payload: MaintenanceScheduleInput = {
        maintenanceType: form.maintenanceType as MaintenanceType,
        intervalMonths: num(form.intervalMonths),
        intervalKm: num(form.intervalKm),
        lastServiceDate: text(form.lastServiceDate),
        lastServiceMileage: num(form.lastServiceMileage),
        notes: text(form.notes),
      };
      return maintenanceApi.saveSchedule(scheduleVehicleId, payload);
    },
    onSuccess: (schedule) => {
      toast.success(`${humanise(schedule.maintenanceType)} interval saved`);
      setScheduleDraft(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.schedules(scheduleVehicleId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.maintenance.all });
    },
    onError: (error) => handleApiError(error),
  });

  const removeSchedule = useMutation({
    mutationFn: (schedule: MaintenanceSchedule) =>
      maintenanceApi.deleteSchedule(schedule.vehicleId, schedule.id),
    onSuccess: () => {
      toast.info('Schedule removed');
      setRemoving(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.schedules(scheduleVehicleId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.maintenance.all });
    },
    onError: (error) => handleApiError(error),
  });

  const openCreate = (prefill?: { vehicleId?: string; maintenanceType?: MaintenanceType }) =>
    setDraft({
      ...EMPTY_RECORD,
      vehicleId: prefill?.vehicleId ?? '',
      maintenanceType: prefill?.maintenanceType ?? '',
    });

  const openEdit = (record: MaintenanceRecord) =>
    setDraft({
      id: record.id,
      vehicleId: record.vehicleId,
      maintenanceType: record.maintenanceType,
      serviceDate: record.serviceDate ?? '',
      mileageKm: record.mileageKm === undefined ? '' : String(record.mileageKm),
      description: record.description ?? '',
      serviceProvider: record.serviceProvider ?? '',
      partsCost: record.partsCost === undefined ? '' : String(record.partsCost),
      laborCost: record.laborCost === undefined ? '' : String(record.laborCost),
      totalCost: record.totalCost === undefined ? '' : String(record.totalCost),
      nextServiceDate: record.nextServiceDate ?? '',
      nextServiceMileage:
        record.nextServiceMileage === undefined ? '' : String(record.nextServiceMileage),
      notes: record.notes ?? '',
    });

  const columns: Column<MaintenanceRecord>[] = [
    {
      key: 'vehicle',
      header: 'Vehicle',
      render: (row) => (
        <span>
          <span className="block text-sm font-semibold text-ink">{row.vehicleName}</span>
          <span className="block text-[11px] text-subtle">{orDash(row.serviceProvider)}</span>
        </span>
      ),
    },
    {
      key: 'work',
      header: 'Work',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{humanise(row.maintenanceType)}</span>
          {row.description !== undefined && (
            <span className="block max-w-[28ch] truncate text-[11px] text-subtle">
              {row.description}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'serviceDate',
      header: 'Service date',
      sortKey: 'serviceDate',
      render: (row) => formatDate(row.serviceDate),
    },
    {
      key: 'mileage',
      header: 'Odometer',
      className: 'text-right',
      render: (row) => (
        <span className="tabular-nums">
          {row.mileageKm === undefined ? '—' : `${row.mileageKm.toLocaleString()} km`}
        </span>
      ),
    },
    {
      key: 'cost',
      header: 'Cost',
      className: 'text-right',
      render: (row) => <Money value={row.totalCost} />,
    },
    {
      key: 'next',
      header: 'Next due',
      render: (row) => (
        <span className="text-xs">
          {row.nextServiceDate !== undefined && <span>{formatDate(row.nextServiceDate)}</span>}
          {row.nextServiceMileage !== undefined && (
            <span className="block tabular-nums text-subtle">
              at {row.nextServiceMileage.toLocaleString()} km
            </span>
          )}
          {row.nextServiceDate === undefined && row.nextServiceMileage === undefined && '—'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <MaintenanceStatusBadge status={row.status} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) =>
        manages ? (
          <span className="flex justify-end gap-1.5">
            {(row.status === 'SCHEDULED' || row.status === 'IN_PROGRESS') && (
              <Button size="sm" variant="ghost" onClick={() => openEdit(row)}>
                Edit
              </Button>
            )}
            {row.status === 'SCHEDULED' && (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setStatus.mutate({ id: row.id, status: 'IN_PROGRESS' })}
                >
                  Start work
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setStatus.mutate({ id: row.id, status: 'CANCELLED' })}
                >
                  Cancel
                </Button>
              </>
            )}
            {row.status === 'IN_PROGRESS' && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setStatus.mutate({ id: row.id, status: 'COMPLETED' })}
              >
                Complete
              </Button>
            )}
          </span>
        ) : null,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Maintenance"
        description="What is due, what is booked, and the standing intervals that decide it."
        icon={<Wrench className="h-5 w-5" aria-hidden="true" />}
        action={manages ? <Button onClick={() => openCreate()}>Record a service</Button> : undefined}
      />

      <Card>
        <CardHeader
          title="Due across the fleet"
          description="Computed from the schedules against today and each odometer — worst first."
        />
        {due.isPending ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : (due.data ?? []).length === 0 ? (
          <p className="px-5 py-6 text-sm text-muted">Nothing is due. The intervals are holding.</p>
        ) : (
          <ul className="divide-y divide-line px-5">
            {(due.data ?? []).map((item) => (
              <li key={item.scheduleId} className="flex flex-wrap items-center gap-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-ink">
                    {item.vehicleName}
                    <span className="ml-2 font-normal text-muted">
                      {humanise(item.maintenanceType)}
                    </span>
                  </p>
                  <p className="text-[11px] text-subtle">
                    {item.dueDate !== undefined && `due ${formatDate(item.dueDate)}`}
                    {item.dueDate !== undefined && item.dueMileage !== undefined && ' · '}
                    {item.dueMileage !== undefined &&
                      `at ${item.dueMileage.toLocaleString()} km (now ${item.currentMileageKm.toLocaleString()})`}
                  </p>
                </div>
                <Badge tone={item.overdue ? 'danger' : 'warning'}>
                  {item.overdue ? 'overdue' : 'due'}
                </Badge>
                {manages && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      openCreate({
                        vehicleId: item.vehicleId,
                        maintenanceType: item.maintenanceType,
                      })
                    }
                  >
                    Book it
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="mt-4 overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 border-b border-line bg-surface-2/60 px-4 py-3">
          <FilterSelect
            label="Vehicle"
            value={vehicleFilter}
            onChange={(value) => {
              setVehicleFilter(value);
              table.setPage(0);
            }}
            options={(vehicles.data?.data ?? []).map((vehicle) => ({
              value: vehicle.id,
              label: vehicle.displayName,
            }))}
          />
          <FilterSelect
            label="Status"
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              table.setPage(0);
            }}
            options={STATUSES}
          />
        </div>

        <DataTable
          columns={columns}
          rows={records.data?.data}
          rowKey={(row) => row.id}
          loading={records.isPending}
          error={records.error}
          onRetry={() => void records.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle="No maintenance records"
          emptyDescription="Nothing has been booked or recorded."
        />

        <Pagination
          pagination={records.data?.pagination}
          onPageChange={table.setPage}
          onSizeChange={table.setSize}
        />
      </Card>

      <Card className="mt-4">
        <CardHeader
          title="Service intervals"
          description="One standing schedule per vehicle and type — every N months and/or kilometres, first bound wins."
          action={
            manages && scheduleVehicleId !== '' ? (
              <Button size="sm" variant="secondary" onClick={() => setScheduleDraft(EMPTY_SCHEDULE)}>
                Set an interval
              </Button>
            ) : undefined
          }
        />
        <div className="px-5 py-4">
          <div className="max-w-xs">
            <SelectField
              label="Vehicle"
              value={scheduleVehicleId}
              onChange={(event) => setScheduleVehicleId(event.target.value)}
            >
              <option value="">Choose a vehicle…</option>
              {vehicles.data?.data.map((vehicle) => (
                <option key={vehicle.id} value={vehicle.id}>
                  {vehicle.displayName}
                </option>
              ))}
            </SelectField>
          </div>

          {scheduleVehicleId === '' ? (
            <p className="py-4 text-sm text-muted">
              Choose a vehicle to see its standing intervals.
            </p>
          ) : schedules.isPending ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : (schedules.data ?? []).length === 0 ? (
            <p className="py-4 text-sm text-muted">
              No intervals yet — this vehicle never appears on the due list.
            </p>
          ) : (
            <ul className="mt-2 divide-y divide-line">
              {(schedules.data ?? []).map((schedule) => (
                <li key={schedule.id} className="flex flex-wrap items-center gap-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-ink">
                      {humanise(schedule.maintenanceType)}
                      <span className="ml-2 font-normal text-muted">
                        {[
                          schedule.intervalMonths !== undefined
                            ? `every ${schedule.intervalMonths} months`
                            : null,
                          schedule.intervalKm !== undefined
                            ? `every ${schedule.intervalKm.toLocaleString()} km`
                            : null,
                        ]
                          .filter((part) => part !== null)
                          .join(' · ')}
                      </span>
                    </p>
                    <p className="text-[11px] text-subtle">
                      last {formatDate(schedule.lastServiceDate)}
                      {schedule.lastServiceMileage !== undefined &&
                        ` at ${schedule.lastServiceMileage.toLocaleString()} km`}
                      {' — next '}
                      {schedule.nextDueDate !== undefined && formatDate(schedule.nextDueDate)}
                      {schedule.nextDueMileage !== undefined &&
                        ` at ${schedule.nextDueMileage.toLocaleString()} km`}
                    </p>
                  </div>
                  {schedule.overdue && <Badge tone="danger">overdue</Badge>}
                  {manages && (
                    <span className="flex gap-1.5">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          setScheduleDraft({
                            maintenanceType: schedule.maintenanceType,
                            intervalMonths:
                              schedule.intervalMonths === undefined
                                ? ''
                                : String(schedule.intervalMonths),
                            intervalKm:
                              schedule.intervalKm === undefined ? '' : String(schedule.intervalKm),
                            lastServiceDate: schedule.lastServiceDate ?? '',
                            lastServiceMileage:
                              schedule.lastServiceMileage === undefined
                                ? ''
                                : String(schedule.lastServiceMileage),
                            notes: schedule.notes ?? '',
                          })
                        }
                      >
                        Edit
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setRemoving(schedule)}>
                        Remove
                      </Button>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>

      <Modal
        open={draft !== null}
        onClose={() => setDraft(null)}
        title={draft?.id === undefined ? 'Record a service' : 'Edit the record'}
        description="Costs are reporting figures — no payment or ledger entry is created."
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button
              loading={save.isPending}
              disabled={draft === null || draft.vehicleId === '' || draft.maintenanceType === ''}
              onClick={() => save.mutate()}
            >
              Save it
            </Button>
          </>
        }
      >
        {draft !== null && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <SelectField
                label="Vehicle"
                required
                value={draft.vehicleId}
                onChange={(event) => edit({ vehicleId: event.target.value })}
              >
                <option value="">Select…</option>
                {vehicles.data?.data.map((vehicle) => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.displayName}
                  </option>
                ))}
              </SelectField>
              <SelectField
                label="Type of work"
                required
                value={draft.maintenanceType}
                onChange={(event) =>
                  edit({ maintenanceType: event.target.value as MaintenanceType | '' })
                }
              >
                <option value="">Select…</option>
                {TYPES.map((value) => (
                  <option key={value} value={value}>
                    {humanise(value)}
                  </option>
                ))}
              </SelectField>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label="Service date"
                type="date"
                value={draft.serviceDate}
                onChange={(event) => edit({ serviceDate: event.target.value })}
              />
              <TextField
                label="Odometer (km)"
                type="number"
                min={0}
                value={draft.mileageKm}
                onChange={(event) => edit({ mileageKm: event.target.value })}
                hint="Completing rolls the vehicle's odometer forward — it never rolls back."
              />
            </div>

            <TextField
              label="What was done"
              value={draft.description}
              onChange={(event) => edit({ description: event.target.value })}
            />
            <TextField
              label="Service provider"
              value={draft.serviceProvider}
              onChange={(event) => edit({ serviceProvider: event.target.value })}
            />

            <div className="grid gap-3 sm:grid-cols-3">
              <TextField
                label="Parts cost"
                type="number"
                min={0}
                value={draft.partsCost}
                onChange={(event) => edit({ partsCost: event.target.value })}
              />
              <TextField
                label="Labour cost"
                type="number"
                min={0}
                value={draft.laborCost}
                onChange={(event) => edit({ laborCost: event.target.value })}
              />
              <TextField
                label="Total cost"
                type="number"
                min={0}
                value={draft.totalCost}
                onChange={(event) => edit({ totalCost: event.target.value })}
                hint="Leave blank: the server takes parts + labour."
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label="Next service date"
                type="date"
                value={draft.nextServiceDate}
                onChange={(event) => edit({ nextServiceDate: event.target.value })}
              />
              <TextField
                label="Next service at (km)"
                type="number"
                min={0}
                value={draft.nextServiceMileage}
                onChange={(event) => edit({ nextServiceMileage: event.target.value })}
              />
            </div>

            <TextField
              label="Notes"
              value={draft.notes}
              onChange={(event) => edit({ notes: event.target.value })}
            />
          </div>
        )}
      </Modal>

      <Modal
        open={scheduleDraft !== null}
        onClose={() => setScheduleDraft(null)}
        title="Set a service interval"
        description="Saving again for the same type replaces the intervals rather than adding a second schedule."
        footer={
          <>
            <Button variant="secondary" onClick={() => setScheduleDraft(null)}>
              Cancel
            </Button>
            <Button
              loading={saveSchedule.isPending}
              disabled={
                scheduleDraft === null ||
                scheduleDraft.maintenanceType === '' ||
                (scheduleDraft.intervalMonths.trim() === '' &&
                  scheduleDraft.intervalKm.trim() === '')
              }
              onClick={() => saveSchedule.mutate()}
            >
              Save it
            </Button>
          </>
        }
      >
        {scheduleDraft !== null && (
          <div className="space-y-3">
            <SelectField
              label="Type of work"
              required
              value={scheduleDraft.maintenanceType}
              onChange={(event) =>
                editSchedule({ maintenanceType: event.target.value as MaintenanceType | '' })
              }
            >
              <option value="">Select…</option>
              {TYPES.map((value) => (
                <option key={value} value={value}>
                  {humanise(value)}
                </option>
              ))}
            </SelectField>

            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label="Every N months"
                type="number"
                min={1}
                value={scheduleDraft.intervalMonths}
                onChange={(event) => editSchedule({ intervalMonths: event.target.value })}
                hint="Months, kilometres, or both — first bound wins."
              />
              <TextField
                label="Every N km"
                type="number"
                min={1}
                value={scheduleDraft.intervalKm}
                onChange={(event) => editSchedule({ intervalKm: event.target.value })}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label="Last serviced on"
                type="date"
                value={scheduleDraft.lastServiceDate}
                onChange={(event) => editSchedule({ lastServiceDate: event.target.value })}
                hint="The anchor the intervals count from."
              />
              <TextField
                label="Last serviced at (km)"
                type="number"
                min={0}
                value={scheduleDraft.lastServiceMileage}
                onChange={(event) => editSchedule({ lastServiceMileage: event.target.value })}
              />
            </div>

            <TextField
              label="Notes"
              value={scheduleDraft.notes}
              onChange={(event) => editSchedule({ notes: event.target.value })}
            />
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={removing !== null}
        title="Remove this interval?"
        message={
          removing === null
            ? ''
            : `${removing.vehicleName} — ${humanise(removing.maintenanceType)} will stop appearing on the due list. Past service records are untouched.`
        }
        confirmLabel="Remove it"
        destructive
        loading={removeSchedule.isPending}
        onConfirm={() => {
          if (removing !== null) {
            removeSchedule.mutate(removing);
          }
        }}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
}
